/**
 * @file Settings.js - Admin page for site-wide settings.
 *
 * @author Steven Secreti
 */
import React, { useEffect, useState } from "react";

import {
	Alert,
	AlertIcon,
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Heading,
	Stack,
	Switch,
	Text,
	Textarea,
	Tooltip,
	useToast,
} from "@chakra-ui/react";

import useGetSiteSettings from "hooks/SiteSettings/queries/useGetSiteSettings";
import useUpdateSiteSettings from "hooks/SiteSettings/mutations/useUpdateSiteSettings";

import LoadingErrorDataWrapper from "wrappers/LoadingErrorDataWrapper";

import ErrorMsg from "Blueprints/ErrorMsg";

const MESSAGE_MAX_LENGTH = 500;
const MESSAGE_PLACEHOLDER = "e.g. Please be aware, we are currently 10-12 weeks out from contract signing to install.";

const Settings = () => {
	const { loading, error, data: settings } = useGetSiteSettings();
	const { updateSiteSettings, loading: saving } = useUpdateSiteSettings();

	const toast = useToast();

	const [enabled, setEnabled] = useState(false);
	const [message, setMessage] = useState("");
	const [formError, setFormError] = useState(null);

	useEffect(() => {
		if (settings?.installLeadTime) {
			setEnabled(settings.installLeadTime.enabled);
			setMessage(settings.installLeadTime.message || "");
		}
	}, [settings]);

	const trimmedMessage = message.trim();

	const handleSave = async () => {
		setFormError(null);

		if (trimmedMessage.length > MESSAGE_MAX_LENGTH) {
			setFormError(`Message cannot exceed ${MESSAGE_MAX_LENGTH} characters.`);
			return;
		}

		const { error: mutationError } = await updateSiteSettings({
			installLeadTime: { enabled, message: trimmedMessage },
		});

		if (mutationError) {
			setFormError(mutationError.message || "Unable to save settings. Please try again.");
			return;
		}

		toast({
			title: "Settings saved",
			description: "Site settings updated successfully.",
			status: "success",
			duration: 4000,
			isClosable: true,
		});
	};

	const saved = settings?.installLeadTime;
	const isDirty = saved
		? enabled !== saved.enabled || trimmedMessage !== (saved.message || "").trim()
		: false;
	const showsBannerOnSite = enabled && trimmedMessage.length > 0;

	return (
		<Flex flexDir="column" w="100%" h="100%" p={{ base: 4, md: 8 }} overflowY="auto">
			<Heading as="h1" size="lg" mb={2}>Site Settings</Heading>
			<Text color="gray.600" mb={6}>
				Manage public-facing notices and site-wide configuration.
			</Text>
			<Divider mb={6} />

			<LoadingErrorDataWrapper loading={loading} error={error?.message} data={settings}>
				<Box
					maxW="2xl"
					borderWidth="1px"
					borderColor="gray.200"
					borderRadius="md"
					boxShadow="sm"
					bg="white"
					p={6}
				>
					<Heading as="h2" size="md" mb={1}>Installation Lead Time Notice</Heading>
					<Text fontSize="sm" color="gray.600" mb={5}>
						Show a banner on the estimate request form with whatever message you want
						customers to see (for example, your current lead time from contract signing
						to install).
					</Text>

					<Stack spacing={5}>
						<FormControl display="flex" alignItems="center">
							<FormLabel htmlFor="lead-time-enabled" mb={0}>
								Show notice on estimate form
							</FormLabel>
							<Switch
								id="lead-time-enabled"
								isChecked={enabled}
								onChange={(e) => setEnabled(e.target.checked)}
								colorScheme="red"
							/>
						</FormControl>

						<FormControl>
							<FormLabel>Banner message</FormLabel>
							<Textarea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder={MESSAGE_PLACEHOLDER}
								maxLength={MESSAGE_MAX_LENGTH}
								rows={3}
							/>
							<FormHelperText>
								{trimmedMessage.length}/{MESSAGE_MAX_LENGTH} characters
							</FormHelperText>
						</FormControl>

						<Box>
							<Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={2}>
								Preview
							</Text>
							{showsBannerOnSite ? (
								<Alert status="info" variant="left-accent" borderRadius="md">
									<AlertIcon />
									{trimmedMessage}
								</Alert>
							) : (
								<Box bg="gray.50" borderRadius="md" p={4} borderWidth="1px" borderColor="gray.200">
									<Text color="gray.600" fontStyle="italic">
										{enabled
											? "Add a message above to display the banner."
											: "Banner is turned off; nothing will show on the estimate form."}
									</Text>
								</Box>
							)}
						</Box>

						{formError && <ErrorMsg>{formError}</ErrorMsg>}

						<Flex justify="flex-end">
							<Tooltip
								label="No changes to save"
								isDisabled={isDirty}
								shouldWrapChildren
								hasArrow
								placement="top"
							>
								<Button
									variant="primary"
									onClick={handleSave}
									isLoading={saving}
									loadingText="Saving"
									isDisabled={!isDirty}
								>
									Save Changes
								</Button>
							</Tooltip>
						</Flex>
					</Stack>
				</Box>
			</LoadingErrorDataWrapper>
		</Flex>
	);
};

export default Settings;
